import * as React from 'react';
import { Field, InjectedFormProps } from 'redux-form';
import { IBrandingState, II18nMessages } from '../../../../../services/branding/models';
import {
  IBilling,
  IChannel,
  ICustomer,
  IOptionMap,
  IPaymentInfo,
  IPaymentWalletProviderConfigData,
  IPremium,
  IProduct,
  IQuotes,
  ITraveller
} from '../../../../../data/api/models';
import { IQuoteActions, IQuoteState } from '../../../../../services/quote/models';
import { get } from 'lodash';
import { FormattedNumber } from 'react-intl';
import FieldFormControl from '../../../../../components/FieldFormControl';
import { isRequired } from '../../../../../util/validators';
import { addSignpostNote } from '../../../../../helpers/signpost';
import DataLayer from '../../../../../helpers/DataLayer';
import { differenceInDays, parse } from 'date-fns';

export interface IPaymentFormProps extends InjectedFormProps {
  i18n: II18nMessages;
  product: IProduct;
  onBack: () => void;
  premium: IPremium;

  renderDefinition(name: string, useProvidedString?: string): any;

  channel: IChannel;
  customer: ICustomer;
  purchaseDeclaration: boolean;
  quote: IQuoteState;
  password: string;
  passwordConfirmation: string;
  policyDeclaration: boolean;
  branding: IBrandingState;
  errorMessage: string;
  schemeId: string;
  paymentInfo: IPaymentInfo;
  startDate: string;
  endDate: string;
  travellers: ITraveller[];
  groupType: string;
  occupations: object;
  industries: object;
  industryValue: string;
  applicationId: string;
  renewalStatus: string;
  documents: [];
  actions: {
    submit(name: string);
    push: (route: string) => void;
    calculate: any;
    change: (form, field, value) => void;
    quote: IQuoteActions;
  };
  post: boolean;
  options?: IOptionMap;
  visibility?: { options: any };
  synchronousError: any;
  payment: IPaymentInfo;
  dispatch: any;
  quoteReference: string;
  reuseCustomerAddress: boolean;
  permissions: boolean;
  quoteType: string;
  quotes: IQuotes;
  medicalsDeclared?: boolean,
  tripType?: string
  billing?: IBilling
  paymentWalletProviderConfigData?: IPaymentWalletProviderConfigData;
}

export class CommonPayment extends React.Component<IPaymentFormProps> {

  constructor(props) {
    super(props);
    const { actions } = props;
    actions.change('quote', 'reuseCustomerAddress', true);
  }

  public componentDidUpdate(prevProps: Readonly<IPaymentFormProps>, prevState: Readonly<{}>, snapshot?: any) {
    const { channel, premium, applicationId, dispatch } = this.props;
    if (prevProps.premium === null && this.props.premium) {
      const isAggregator = channel.channelType === 'AGG';
      const isSignposted = get(premium, 'isSignposted', false);
      if (isAggregator && isSignposted && applicationId) {
        dispatch(addSignpostNote('Medical load over threshold – directory signposted', 'policy-applications', applicationId, 'medical_load'));
      }
    }
  }

  public componentDidMount() {
    DataLayer.handlePageView();
    if (!window) {
      return;
    }
  }

  public componentWillMount() {
    const { branding, actions } = this.props;

    if (branding.channel.channelType === 'AGG') {
      actions.change('quote', 'communications.post', false);
    }

    actions.change('quote', 'permissions', true);
  }

  protected checkForErrors = () => {
    const { actions, travellers, medicalsDeclared } = this.props;

    return Promise.resolve(actions.submit('quote')).then(() => {
      if (!get(travellers, '[0].address.line1', null)) {
        document.getElementById('form-errors').scrollIntoView({ block: 'center', behavior: 'smooth' });
      }

      if (medicalsDeclared === undefined) {
        document.getElementById('form-errors').scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  protected renderPremium = (isAggregator: boolean = false) => {
    const { premium } = this.props;

    if (!premium) {
      return null;
    }

    const discountAmount = Math.abs(premium.discount * (1 + (premium.taxRate / 100)));
    const paymentText = isAggregator ?
      <div>You are also agreeing to our <a
        href="https://www.admiral.com/insurance/your-policy/conditions-of-use.php?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
        target="_blank">conditions of use </a> and <a
        href="https://www.admiral.com/your-privacy-and-security/?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
        target="_blank">privacy statement</a>.</div> : '';

    return (
      <>
        <div className="payment-total-price">
          <div className="total-pay-text"><b>Total to pay</b></div>
          <div className={'total-pay-value'}>
            <FormattedNumber
              value={premium.gross}
              style="currency"
              currency={premium.currency}
            />
            {discountAmount > 0 &&
              <div className="quote-info__discount info-note">
                Including a discount of{' '}
                <FormattedNumber
                  value={discountAmount}
                  style="currency"
                  currency={premium.currency}
                />
              </div>
            }
          </div>
        </div>
        <div className={'payment-price-info'}>
          <div>
            This price includes IPT of 20% (Where applicable). By continuing you are agreeing to the Terms and
            Conditions
            shown above and that the summary of cover meets your needs.
          </div>
          <div>
            {paymentText}
          </div>
        </div>
      </>
    );
  }

  protected renderTrackingItems = () => {
    const { branding, premium } = this.props;

    const items = [];

    if (branding.aggregatorTracking.type === 'cyti' && premium) {
      const params = branding.aggregatorTracking.cyti;
      const cytiUrl = params.url + '/track/click';

      items.push(
        <img
          key={0}
          src={`${cytiUrl}?cref=${params.cref}&premium=${premium.gross}&t=${params.token}`}
          height="0"
          width="0"
        />,
      );
    }

    return items;
  }

  protected isInWalletTestGroup = (): boolean => {
    const { quote } = this.props;
    const dob = quote.application.travellers[0] && quote.application.travellers[0].dob;
    if (!dob) {
      return false;
    }
    const diffDays = differenceInDays(parse(dob, 'dd/MM/yyyy', new Date()), new Date('1901-01-01'));
    const lastDigit = Math.abs(Math.round(diffDays * (997 / 1000))) % 10;
    return lastDigit <= 4;
  };

  protected hasTravellerMedical = () => {
    const { travellers } = this.props;
    if (travellers) {
      return travellers.some((traveller) => {
        return traveller.screening;
      });
    }
    return false;
  }

  protected trackDocumentPreference = (documentPreference: 'online' | 'post' | 'personal' | 'full') => {
    DataLayer.pushLinkClicked(documentPreference, this.props.quoteReference);
  }

  protected trackDocumentPreferenceOnline = () => this.trackDocumentPreference('online');

  protected trackDocumentPreferencePost = () => this.trackDocumentPreference('post');

  protected trackDocumentPackPersonal = () => this.trackDocumentPreference('personal');

  protected trackDocumentPackFull = () => this.trackDocumentPreference('full');

  protected renderPrintOptions = () => {
    const { post } = this.props;

    return (
      <div>
        <div>
          <div className={'policy-doc-prefs-info'}>
            <img src={require('../../../../../images/icons/world-forest.svg')} alt={'Policy Document Preferences icon'}
                 className={'policy-doc-prefs-icon'}/>
            <div>
              <p className={'normal'}>Choosing online documents means less paper waste and less pollution.
                One small choice can help make a big difference.
              </p>

              <div className={'radio-group margin-top policy-document-buttons'}>
                <div className={'option-new'}>
                  <div className="radio-button radio-button-square">
                    <Field
                      name="communications.post"
                      type="radio"
                      value={false}
                      complexValue={true}
                      component={FieldFormControl}
                      validate={[isRequired]}
                  onChange={this.trackDocumentPreferenceOnline}
                    >
                      <div/>
                    </Field>
                  </div>
                  <div className="radio-text">
                    <div>{'Documents Online, easy to access and ready to download'}</div>
                  </div>

                </div>
                <div className={'docs-online-list'}>
                  <ul>
                    <li>View and download your documents instantly on your Travel Portal</li>
                    <li>Access them anytime through your online account</li>
                    <li>Make changes online without needing to contact us</li>
                    <li>When we need to contact you, we'll send you an email (including your renewal).</li>
                  </ul>
                </div>
                <div className={'option-new'}>
                  <div className="radio-button radio-button-square">
                    <Field
                      name="communications.post"
                      type="radio"
                      value={true}
                      complexValue={true}
                      component={FieldFormControl}
                      validate={[isRequired]}
                  onChange={this.trackDocumentPreferencePost}
                    >
                      <div/>
                    </Field>
                  </div>
                  <div className="radio-text">
                    {'Send Documents by post (please allow up to 5 working days)'}
                  </div>
                </div>
              </div>
              {post && (
                <div>
                  <div style={{ marginTop: '10px' }}> Please select which option you would prefer?</div>
                  <div className={'radio-group margin-top'}>
                    <div className={'option-new'} style={{ width: '100%' }}>
                      <div className="radio-button radio-button-square">
                        <Field
                          name="documentPack"
                          type="radio"
                          value={'personal'}
                          complexValue={true}
                          component={FieldFormControl}
                          validate={[isRequired]}
                          onChange={this.trackDocumentPackPersonal}
                        >
                          <div/>
                        </Field>
                      </div>
                      <div className="radio-text">
                        I’d like to receive my personal policy documents in the post but I’m happy to access the policy
                        booklet and Insurance Product Information Documents online.
                      </div>
                    </div>
                    <div className={'option-new'} style={{ width: '100%', marginTop: '15px' }}>
                      <div className="radio-button radio-button-square">
                        <Field
                          name="documentPack"
                          type="radio"
                          value={'full'}
                          complexValue={true}
                          component={FieldFormControl}
                          validate={[isRequired]}
                          onChange={this.trackDocumentPackFull}
                        >
                          <div/>
                        </Field>
                      </div>
                      <div className="radio-text">
                        I’d like to receive all policy documents in the post, including the policy booklet and Insurance
                        Product Information Document.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export const validate = (values) => {
  const errors = {
    passwordConfirmation: null,
    marketing: {
      accepted: null
    },
    policyDeclaration: null,
    travellers: [
      {
        address: {
          postcode: null
        }
      }
    ]
  };
  if (values.passwordConfirmation !== values.password) {
    errors.passwordConfirmation = 'Your confirmed password doesn\'t match the first field, please enter them again.';
  }
  if (!values.policyDeclaration) {
    errors.policyDeclaration = 'Please accept the policy declaration';
  }
  if (!get(values, 'travellers[0].address.line1', null)) {
    errors.travellers[0].address.postcode = 'Please use the Lookup button to select an address.';
  }

  if (get(values, 'marketing.accepted', null)
    && (
      !get(values, 'marketing.options.post', null)
      && !get(values, 'marketing.options.sms', null)
      && !get(values, 'marketing.options.email', null)
      && !get(values, 'marketing.options.phone', null)
    )
  ) {
    errors.marketing.accepted = 'Please select at least one method of contact.';
  }

  return errors;
};


